import axios from 'axios';
import { getCookie } from 'cookies-next';
import getConfig from 'next/config';

export type InboxItem = {
  id: string;
  title: string;
  body: string;
  url: string | null;
  created_at: string;
  read: boolean;
};

export type InboxResponse = {
  items: InboxItem[];
  unread_count: number;
  next_cursor: string | null;
};

export const inboxQueryKey = ['inbox'] as const;

const DEFAULT_NOTIFICATIONS_API_URL = 'https://notifications.paziresh24.com';

export function notificationsApiBase(): string {
  try {
    const { publicRuntimeConfig } = getConfig();
    return String(publicRuntimeConfig?.NOTIFICATIONS_API_URL || DEFAULT_NOTIFICATIONS_API_URL).replace(/\/$/, '');
  } catch {
    return DEFAULT_NOTIFICATIONS_API_URL;
  }
}

export function notificationsApiConfigured(): boolean {
  return notificationsApiBase() !== '';
}

function authToken(): string | undefined {
  const gozargah = getCookie('gozargah_token');
  if (typeof gozargah === 'string' && gozargah) return gozargah;
  const legacy = getCookie('token');
  if (typeof legacy === 'string' && legacy) return legacy;
  return undefined;
}

function sessionRequest() {
  const headers: Record<string, string> = {};
  const token = authToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  if (typeof window !== 'undefined') {
    const preview = window.localStorage.getItem('fragment::previewToken');
    if (preview) headers.Authorization = `Bearer ${preview}`;
  }
  return { withCredentials: true as const, headers };
}

export async function getInbox(): Promise<InboxResponse> {
  const { data } = await axios.get<InboxResponse>(`${notificationsApiBase()}/me/messages`, sessionRequest());
  return data;
}

export async function markInboxRead(id: string): Promise<void> {
  await axios.post(`${notificationsApiBase()}/me/messages/${id}/read`, undefined, sessionRequest());
}

/** Counts a click on the notification (analytics only; never blocks the user). */
export async function recordInboxClick(id: string): Promise<void> {
  try {
    await axios.post(`${notificationsApiBase()}/me/messages/${id}/click`, undefined, sessionRequest());
  } catch {
    // Missing on older service builds, or offline: the click just isn't counted.
  }
}

export async function markAllInboxRead(): Promise<void> {
  try {
    await axios.post(`${notificationsApiBase()}/me/messages/read-all`, undefined, sessionRequest());
  } catch (error) {
    // Older service builds have no read-all: mark the loaded page one by one.
    if (!axios.isAxiosError(error) || error.response?.status !== 404) throw error;
    const inbox = await getInbox();
    await Promise.allSettled(inbox.items.filter(item => !item.read).map(item => markInboxRead(item.id)));
  }
}

// Plasmic components still call the old Hamdast notifications API through
// Fragment.apiRequest / ApiRequest; these helpers reroute them to the inbox.
const LEGACY_NOTIFICATIONS_URL = /^https?:\/\/apigw\.paziresh24\.com\/v1\/hamdast\/notifications\/?(\?.*)?$/;
const LEGACY_MARK_READ_URL = /^https?:\/\/apigw\.paziresh24\.com\/v1\/hamdast\/notifications\/mark-as-read(?:\/([^/?#]+))?\/?(\?.*)?$/;

export function isLegacyInboxUrl(url: string): boolean {
  return LEGACY_NOTIFICATIONS_URL.test(url);
}

/** `null` = not a legacy mark-read URL, `'all'` = mark every item, otherwise the item id. */
export function legacyMarkReadId(url: string): string | null {
  const match = url.match(LEGACY_MARK_READ_URL);
  if (!match) return null;
  return match[1] ? decodeURIComponent(match[1]) : 'all';
}

export function toLegacyInboxBody(inbox: InboxResponse) {
  return {
    items: inbox.items.map(item => ({
      id: item.id,
      title: item.title,
      description: item.body,
      link: item.url,
      created: item.created_at,
      read: item.read,
    })),
    unread_count: inbox.unread_count,
  };
}

/** Links this phone's native (FCM) push token to the logged-in user; idempotent upsert. */
export async function registerAndroidDevice(token: string): Promise<void> {
  await axios.put(
    `${notificationsApiBase()}/me/devices`,
    { client: 'android', push_subscription: { token, spec_version: 1 } },
    sessionRequest(),
  );
}

/**
 * Claims the token the app left under `code` when it couldn't reach the open page directly
 * (app process restarted); links the phone to the logged-in user. Rejects with 404 until the
 * app has dropped it.
 */
export async function claimAndroidDeviceHandoff(code: string): Promise<{ granted: boolean }> {
  const { data } = await axios.post<{ granted: boolean }>(
    `${notificationsApiBase()}/me/device-handoffs/${code}/claim`,
    undefined,
    sessionRequest(),
  );
  return data;
}
